import React, { useCallback, useEffect, useState } from "react";
import "./ShoppingCart.css";
import { useSelector } from "react-redux";
import {
  selectCartItems,
  selectCartTotalAmount,
  selectTotalDiscount,
  selectOriginalTotal,
} from "../../Features/Cart/cartSlice";
import { Link, useNavigate } from "react-router-dom";
import useAuth from "../../Features/Utils/useAuth";
import { useLoginModal } from "../../App/LoginModalProvider";
import {
  addressCreate,
  addressDelete,
  addressDeliver,
  addressUpdate,
  getAddressList,
  toastError,
  toastSuccess,
  addressDeliverDefault,
  createOrder,
} from "../../api/services";
import { AiOutlineCheck } from "react-icons/ai";
import {
  checkGPSPermission,
  getAddressFromLocal,
} from "../../Features/Utils/locationHelper";
import UnifiedAddressModal from "./UnifiedAddressModal";
import AddressButton from "./AddressButton";
import StepProgressUI from "./StepProgressUI";

const ViewCheckout = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const { openLoginModal } = useLoginModal();

  // Redux se cart data
  const cartItems = useSelector(selectCartItems);
  const totalAmount = useSelector(selectCartTotalAmount);
  const totalDiscount = useSelector(selectTotalDiscount);
  const originalTotal = useSelector(selectOriginalTotal);

  // Address related states
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [gpsAvailable, setGpsAvailable] = useState(true);
  const [editAddressId, setEditAddressId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deliverId, setDeliverId] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [step, setStep] = useState(2);
  const [continueLoading, setContinueLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deliverLoading, setDeliverLoading] = useState(false);
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [error, setError] = useState("");
  const [editAddressData, setEditAddressData] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [addressesDefault, setAddressesDefault] = useState(null);

  // Fetch default address
  const fetchDefaultAddress = useCallback(async () => {
    try {
      const responseDefault = await addressDeliverDefault(user?.id);
      if (responseDefault?.data?.success) {
        setAddressesDefault(responseDefault?.data?.data || null);
      } else {
        setShowAddressModal(true);
      }
    } catch (error) {
      console.error("Error fetching default address:", error);
      setShowAddressModal(true);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) fetchDefaultAddress();
  }, [fetchDefaultAddress, user?.id]);

  // GPS and saved address
  useEffect(() => {
    const loadSavedAddress = async () => {
      const savedAddress = getAddressFromLocal();
      if (savedAddress) setSelectedAddress(savedAddress);
      const status = await checkGPSPermission();
      setGpsAvailable(status !== "denied");
    };
    loadSavedAddress();
  }, []);

  const handleOpenAddressModal = async () => {
    setIsAddressLoading(true);
    if (isAuthenticated) {
      await fetchAddresses();
    } else {
      await openLoginModal();
      setIsAddressLoading(false);
    }
  };

  const fetchAddresses = useCallback(async () => {
    if (!user?.id) return;
    setError("");
    try {
      const response = await getAddressList({ userId: user?.id });
      if (response?.data?.success) {
        setAddresses(response?.data?.data || []);
        setViewId("viewAddress");
      } else {
        setError(response?.data?.message || "Failed to load addresses");
      }
    } catch (err) {
      console.error("Fetch addresses error:", err);
      setError("Network error. Please try again.");
    } finally {
      setIsAddressLoading(false);
    }
  }, [user?.id]);

  // ✅ Place Order: create order and go to payment
  const handlePlaceOrder = async () => {
    if (cartItems.length === 0) {
      toastError("Your cart is empty");
      return;
    }
    if (!addressesDefault?.id) {
      toastError("Please select a delivery address");
      return;
    }

    setContinueLoading(true);
    try {
      const payload = {
        userId: user?.id,
        amount: totalAmount,
        totalAmount: totalAmount,
        totalDiscount: totalDiscount,
        originalTotal: originalTotal,
        discount: 0,
        platformFee: originalTotal === 0 ? 0 : 7,
        paymentMethod: "cod", // you can make this dynamic later
        items: cartItems,
        addressId: addressesDefault.id,
      };
      const res = await createOrder(payload);
      if (res?.data?.success) {
        toastSuccess("Order created! Redirecting to payment...");
        const orderId = res.data.data.orderId;
        navigate(`/payment?orderId=${orderId}`);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toastError(res?.data?.message || "Order creation failed");
      }
    } catch (error) {
      console.error(error);
      toastError("Network error. Please try again.");
    } finally {
      setContinueLoading(false);
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // Step handling (optional)
  const handleStepChange = (newStep) => {
    if (newStep === 1) navigate("/cart");
    else if (newStep === 3) handlePlaceOrder();
    else setStep(newStep);
  };

  // Address modal handlers (same as before)
  const handleEdit = (address) => {
    setEditAddressId(address.address_id);
    setEditAddressData(address);
    setShowAddressModal(true);
  };
  const handleAdd = () => {
    setEditAddressId(null);
    setEditAddressData(null);
    setShowAddressModal(true);
  };
  const handleDeliver = async () => {
    if (!deliverId) return;
    setDeliverLoading(true);
    try {
      await addressDeliver(user.id, deliverId);
      toastSuccess("Address set as default");
      await fetchAddresses();
      await fetchDefaultAddress();
      setDeliverId(null);
    } catch (err) {
      toastError("Failed to set default address");
    } finally {
      setDeliverLoading(false);
    }
  };
  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await addressDelete({ id: deleteId });
      toastSuccess("Address deleted");
      await fetchAddresses();
      setDeleteId(null);
    } catch (err) {
      toastError("Failed to delete address");
    } finally {
      setDeleteLoading(false);
    }
  };
  const formatFullAddress = (item) =>
    item
      ? `${item.street || ""}, ${item.city || ""}, ${item.state || ""} ${item.zipCode || ""}, ${item.country || ""}`
      : "";
  const getDisplayAddress = () =>
    addressesDefault?.name
      ? `${addressesDefault.name} - ${addressesDefault.zipCode}`
      : "Select address";
  const getDisplayAddressDefault = () =>
    addressesDefault?.full_address || "Address full_address";

  const itemCount = cartItems.length;

  return (
    <div>
      <div className="container">
        <div className="containerShoppingU">
          <div className="left">
            <div className="deliveryPaymentView">
              <StepProgressUI
                currentStep={step}
                onStepChange={handleStepChange}
              />
            </div>
            <div className="delivery">
              <div>
                <h4>
                  Deliver to: <strong>{getDisplayAddress()}</strong>
                </h4>
                <p>{getDisplayAddressDefault()}</p>
              </div>
              <AddressButton
                isAddressLoading={isAddressLoading}
                selectedAddress={selectedAddress}
                handleOpenAddressModal={handleOpenAddressModal}
              />
            </div>
            {!gpsAvailable && (
              <div className="gps-warning-banner">
                ⚠️ GPS is blocked. Please enable location.
              </div>
            )}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(1, 1fr)",
                gap: "1px",
              }}
            >
              {itemCount > 0 ? (
                cartItems.map((item) => (
                  <div className="cardFood" key={item.productID}>
                    <div className="card-top">
                      <div className="left-box">
                        <Link to="/product" onClick={scrollToTop}>
                          <img src={item.frontImg} alt="" />
                        </Link>
                      </div>
                      <div className="details">
                        <Link to="/product" onClick={scrollToTop}>
                          <h3>{item.productName}</h3>
                        </Link>
                        <p className="sub">{item.productReviews}</p>
                        <div className="rating">☆ 4.2 (39)</div>
                        <div className="price">
                          <span className="off">44% off</span>
                          <span className="old">₹{item.productPrice}</span>
                          <span className="new">
                            ₹{item.quantity * item.productPrice}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="cardFood">
                  <div className="card-top">
                    <div className="left-box">
                      <div className="shoppingCartEmpty">
                        <span>Your cart is empty!</span>
                        <Link to="/" onClick={scrollToTop}>
                          <button>Shop Now</button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="right">
            <h3>Price Details</h3>
            <div className="rowFood">
              <span>
                Price ({itemCount} item{itemCount !== 1 ? "s" : ""})
              </span>
              <span>₹{originalTotal.toFixed(2)}</span>
            </div>
            <div className="rowFood green">
              <span>Discount</span>
              <span>-₹{totalDiscount.toFixed(0)}</span>
            </div>
            <div className="rowFood">
              <span>Platform Fee</span>
              <span>₹{(originalTotal === 0 ? 0 : 7).toFixed(0)}</span>
            </div>
            <hr />
            <div className="rowFood total">
              <span>Total Amount</span>
              <span>₹{totalAmount.toFixed(2)}</span>
            </div>
            <div className="saveCart">
              You'll save ₹{totalDiscount.toFixed(0)} on this order!
            </div>
            {isAuthenticated ? (
              <button
                className="orderSummary"
                onClick={handlePlaceOrder}
                disabled={continueLoading || itemCount === 0}
              >
                {continueLoading ? (
                  <span className="spinner"></span>
                ) : (
                  "Place Order"
                )}
              </button>
            ) : (
              <button
                className="orderSummary"
                onClick={(e) => {
                  e.preventDefault();
                  openLoginModal();
                }}
              >
                Place Order
              </button>
            )}
          </div>
        </div>
      </div>

      {viewId && (
        <div className="modal-overlay">
          <div className="modal-container modal-md">
            <div className="modal-content-custom">
              <div className="modal-header-custom">
                <h4>View Address</h4>
                <button
                  type="button"
                  className="close-btn"
                  onClick={() => setViewId(null)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body-custom">
                <div className="step-body">
                  {error && !deleteId && !deliverId && (
                    <div className="alert alert-danger">{error}</div>
                  )}
                  <div className="orderStepAddress">
                    {addresses.map((addr) => (
                      <div key={addr.address_id} className="address">
                        <div className="addr-details">
                          <div
                            style={{
                              position: "relative",
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <h4>{addr.label}</h4>
                            {addr?.isDefault === 1 && (
                              <div
                                style={{
                                  width: "24px",
                                  height: "25px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background: "#0a0f0f",
                                  borderRadius: "50%",
                                }}
                              >
                                <AiOutlineCheck
                                  style={{
                                    color: "#ffffff",
                                    fontSize: "14px",
                                  }}
                                />
                              </div>
                            )}
                          </div>
                          <p>
                            {formatFullAddress(addr)}
                            <br />
                            {addr.order_note && (
                              <>
                                Note: {addr.order_note}
                                <br />
                              </>
                            )}
                          </p>

                          <div className="address-actions">
                            <button
                              type="button"
                              className="btn address-actions-button"
                              onClick={() => setDeliverId(addr.address_id)}
                              disabled={deliverLoading}
                            >
                              Deliver Here
                            </button>
                            <button
                              type="button"
                              className="btn address-actions-button"
                              onClick={() => handleEdit(addr)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn address-actions-button"
                              onClick={() => setDeleteId(addr.address_id)}
                              disabled={deleteLoading}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleAdd}
                >
                  + Add a new address
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setViewId(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {deliverId && (
        <div className="modal-overlay">
          <div className="modal-container modal-sm">
            <div className="modal-content-custom">
              <div className="modal-header-custom">
                <h4>Set as Default Address</h4>
                <button
                  type="button"
                  className="close-btn"
                  onClick={() => setDeliverId(null)}
                  disabled={deliverLoading}
                >
                  ×
                </button>
              </div>
              <div className="modal-body-custom">
                <p className="deleteAddressP">
                  Are you sure you want to set this address as default for
                  delivery?
                </p>
                {error && (
                  <div className="alert alert-danger mt-2">{error}</div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeliver}
                  disabled={deliverLoading}
                >
                  {deliverLoading ? (
                    <span className="spinner"></span>
                  ) : (
                    "Yes, Set Default"
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDeliverId(null)}
                  disabled={deliverLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {deleteId && (
        <div className="modal-overlay">
          <div className="modal-container modal-sm">
            <div className="modal-content-custom">
              <div className="modal-header-custom">
                <h4>Delete Address</h4>
                <button
                  type="button"
                  className="close-btn"
                  onClick={() => setDeleteId(null)}
                  disabled={deleteLoading}
                >
                  ×
                </button>
              </div>
              <div className="modal-body-custom">
                <p className="deleteAddressP">
                  Are you sure you want to delete this address?
                </p>
                {error && (
                  <div className="alert alert-danger mt-2">{error}</div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <span className="spinner"></span>
                  ) : (
                    "Yes Delete"
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDeleteId(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <UnifiedAddressModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        existingAddress={editAddressData}
        editAddressId={editAddressId}
        userId={user?.id || 0}
        addressCreate={addressCreate}
        addressUpdate={addressUpdate}
        fetchAddresses={fetchAddresses}
        addresses={addresses}
      />
    </div>
  );
};

export default ViewCheckout;
